import { Scene, Vector3, AbstractMesh, MeshBuilder, StandardMaterial, Color3, TransformNode, Quaternion } from '@babylonjs/core';
import { CharacterLoader, CharacterAsset } from '../assets/CharacterLoader';
import { isEmbeddedReference } from '../assets/EntityAssetExtractor';
import { AnimationController } from './AnimationController';
import { CharacterConfig, type DamageContext } from '../config/characters';
import { SplineConstraint } from './SplineConstraint';
import { PhysicsManager } from './physics';
import { CollisionGroups, CollisionMasks } from './physics/IPhysicsEngine';
import { DebugRenderer } from '../core/DebugRenderer';
import { logger } from '../core/Logger';
import { EntityRegistry } from '../core/EntityRegistry';
import { ServiceRegistry } from '../core/ServiceRegistry';
import type { CharacterLifecycleAction, CharacterLifecycleScript, CharacterHealthChangeEvent } from '../config/lifecycle';
import { runLifecycleScripts } from '../config/lifecycle';
// XState integration
import { StateManager, ActionHandlerRegistry, type CharacterActor, registerPhysicsHandler } from '../state';
// Entity type system
import { EntityType, CharacterSubtype } from '../types/EntityTypes';


export interface ICharacterInput {
  getMovementInput(): number; // -1, 0, or 1 (horizontal)
  getVerticalInput(): number; // -1, 0, or 1 (vertical for side-scroller)
  isJumpPressed(): boolean;
  isAttackPressed(): boolean;
  getCameraForward(): Vector3;
  getCameraRight(): Vector3;
}

type DamageApplicationDetails = {
  readonly damageContext?: DamageContext;
  readonly metadata?: Record<string, unknown>;
};

export abstract class Character {
  protected scene: Scene;
  protected splineConstraint: SplineConstraint;
  protected physicsManager: PhysicsManager | null = null;
  protected physicsBody: import('./physics/IPhysicsEngine').IPhysicsBody | null = null; // Phase 7: Dynamic physics body reference
  
  // Character components
  public character: AbstractMesh; // The physics/collision capsule
  protected characterAsset: CharacterAsset | null = null;
  protected characterVisualRoot: TransformNode | null = null;
  public animationController: AnimationController | null = null;
  protected characterConfig: CharacterConfig;
  private damageCallback?: CharacterConfig['onDamageReceived'];
  private attackStartCallback?: CharacterConfig['onAttackStart'];
  private attackHitCallback?: CharacterConfig['onAttackHit'];
  private hitReactionCallback?: CharacterConfig['onHitReaction'];
  private lifecycleScripts: CharacterLifecycleScript[] = [];
  private lifecycleDeathAnimationPlaying = false;
  private pendingDespawn: { mode: 'immediate' | 'after-animation'; delaySeconds: number; awaitingAnimation: boolean } | null = null;
  private pendingDespawnReady = false;
  // Note: isDead is derived from XState health state via getIsDead()
  private hitStaggered = false; // True while hit reaction animation is playing

  /** Check if character is dead (HP <= 0) - derived from XState health state */
  public getIsDead(): boolean {
    if (!this.xstateActor) {
      // Fallback if XState not initialized yet
      return this.hp <= 0;
    }
    const snapshot = this.xstateActor.getSnapshot();
    const healthState = (snapshot.value as { health?: string })?.health;
    return healthState === 'dead';
  }

  public getAssetDebugSummary():
    | {
        instanceId: string;
        configId: string;
        baseAssetPath: string;
        rootMeshName: string;
        meshes: number;
        skeletons: number;
        bones: number;
        morphTargetMeshes: number;
        morphTargets: number;
        morphTargetVertexMismatches: number;
        animationGroups: number;
        phaseAnimations: string[];
        availableAnimationKeys: string[];
        submeshes: Array<{ id: string; rootMeshName: string; animations: number }>;
        configuredAnimationFiles: Array<{ phase: string; file: string }>;
      }
    | null {
    if (!this.characterAsset) return null;

    const meshes = this.characterAsset.meshes;
    const skeleton = this.characterAsset.skeleton;
    const bones = skeleton?.bones?.length ?? 0;

    let morphTargetMeshes = 0;
    let morphTargets = 0;
    let morphTargetVertexMismatches = 0;

    for (const mesh of meshes) {
      const mtm = (mesh as any).morphTargetManager;
      if (!mtm) continue;

      morphTargetMeshes += 1;
      const numTargets: number = mtm.numTargets ?? mtm.getNumTargets?.() ?? 0;
      morphTargets += numTargets;

      const meshVerts = mesh.getTotalVertices ? mesh.getTotalVertices() : 0;
      if (meshVerts > 0 && numTargets > 0 && mtm.getTarget) {
        for (let i = 0; i < numTargets; i++) {
          const mt = mtm.getTarget(i);
          if (!mt) continue;
          const positions = mt.getPositions?.();
          if (!positions) continue;
          const mtVerts = Math.floor(positions.length / 3);
          if (mtVerts !== meshVerts) morphTargetVertexMismatches += 1;
        }
      }
    }

    const phaseAnimations = Array.from(this.characterAsset.phaseMap.keys()).sort();
    const availableAnimationKeys = Array.from(this.characterAsset.animations.keys()).sort();

    const submeshes = Array.from(this.characterAsset.submeshes.entries()).map(([id, sm]) => ({
      id,
      rootMeshName: sm.rootMesh.name,
      animations: sm.animations.size,
    }));

    const configuredAnimationFiles: Array<{ phase: string; file: string }> = [];
    const mapping = this.characterConfig.animations;
    if (mapping) {
      for (const [phase, source] of Object.entries(mapping)) {
        if (!source) continue;
        if (typeof source === 'string') continue;
        const file = (source as any).file;
        if (typeof file === 'string' && file.length > 0) {
          configuredAnimationFiles.push({ phase, file });
        }
      }
    }

    return {
      instanceId: this.characterId,
      configId: this.characterConfig.id,
      baseAssetPath: this.characterConfig.assetPath,
      rootMeshName: this.characterAsset.rootMesh.name,
      meshes: meshes.length,
      skeletons: skeleton ? 1 : 0,
      bones,
      morphTargetMeshes,
      morphTargets,
      morphTargetVertexMismatches,
      animationGroups: this.characterAsset.animations.size,
      phaseAnimations,
      availableAnimationKeys,
      submeshes,
      configuredAnimationFiles,
    };
  }

  // XState Actor - Primary State Machine (Sprint 0024)
  protected xstateActor: CharacterActor | null = null;
  
  // Track last input state for edge detection
  private lastMovementInput: number = 0;
  private lastJumpInput: boolean = false;

  // Physics State
  public velocity: Vector3 = new Vector3(0, 0, 0);
  private _verticalVelocity: number = 0; // Isolated Y velocity - cannot be externally corrupted
  public desiredMovement: Vector3 = new Vector3(0, 0, 0); // Set by XState-driven movement calculation
  private jumpRequested: boolean = false; // Set by XState playJumpAnimation action
  public isGrounded: boolean = false;
  public playerT: number = 0.5;
  protected lastClosestIndex: number = 0; // Track closest spline index for this character

  // Grounding stability (prevents brief FALL flicker on slopes)
  private groundedGraceRemainingSeconds: number = 0;
  private readonly GROUNDED_GRACE_TIME_SECONDS = 0.12;
  private lastKnownGroundY: number | null = null;
  private readonly FALLTHROUGH_SNAP_THRESHOLD = 0.75;
  
  // Debug rendering (protected for subclass access)
  protected debugFacingArrow: AbstractMesh | null = null;
  protected debugFacingAnchor: TransformNode | null = null;
  protected debugFacingLabel: AbstractMesh | null = null;
  protected debugVelocityLine: AbstractMesh | null = null;
  protected debugTangentArrow: AbstractMesh | null = null;
  protected debugTangentLabel: AbstractMesh | null = null;
  protected debugCurrentSegment: AbstractMesh | null = null;
  protected debugAngleArc: AbstractMesh | null = null; // Arc showing angle between facing and tangent
  
  // Debug rendering enabled flag - when false, no debug meshes are created/updated
  private debugEnabled: boolean = false;
  
  // Debug logging counter (removed in Phase 7 motor physics refactor)
  // private _debugLogCount: number = 0;
  // private _maxDebugLogs: number = 0; // Set to 3 for debugging, 0 to disable
  
  // Current facing direction (updated during movement)
  private currentForwardDirection: Vector3 = new Vector3(0, 0, 1);
  
  // Track last movement direction along spline (1 = positive T, -1 = negative T)
  // Used to maintain facing direction when stopped
  protected lastMovementDirection: number = 1;
  
  // Health tracking
  private hp: number = 100;
  private maxHp: number = 100;
  
  /**
   * Get the current facing direction along the spline.
   * 1 = facing positive T direction, -1 = facing negative T direction
   */
  public getFacingDirection(): number {
    return this.lastMovementDirection;
  }
  
  /**
   * Get the unique character ID.
   */
  public getId(): string {
    return this.characterId;
  }
  
  /**
   * Get current HP.
   */
  public getHp(): { current: number; max: number } {
    return { current: this.hp, max: this.maxHp };
  }
  
  /**
   * Override max health (for map-authored spawns).
   * Must be called before initXStateActor() for XState to receive the correct value.
   * @param maxHealth New max health value
   * @param startHealth Optional starting health (defaults to maxHealth if not specified)
   */
  public setHealth(maxHealth: number, startHealth?: number): void {
    this.maxHp = Math.max(1, maxHealth);
    this.hp = startHealth !== undefined ? Math.max(0, Math.min(startHealth, this.maxHp)) : this.maxHp;
    logger.character.debug(`Character ${this.characterId} health set to ${this.hp}/${this.maxHp}`);
  }
  
  /**
   * @deprecated Use setHealth() instead
   */
  public setMaxHealth(maxHealth: number): void {
    this.setHealth(maxHealth);
  }
  
  /**
   * Disable collision for this character (used when dead).
   * Sets collision mask to 0 so the character won't collide with anything.
   */
  public disableCollision(): void {
    const physicsBodyId = `character_physics_${this.characterId}`;
    if (this.physicsManager) {
      // Set collision mask to 0 to disable all collisions
      this.physicsManager.setCollisionFilter(physicsBodyId, undefined, 0);
      logger.character.debug(`Character ${this.characterId} collision disabled`);
    }
  }
  
  /**
   * Apply damage to this character.
   * @param amount Damage amount (positive number)
   * @param details Optional context for lifecycle observers
   * @returns true if character was killed, false otherwise
   */
  public takeDamage(amount: number, details?: DamageApplicationDetails): boolean {
    const appliedDamage = Math.max(0, amount);
    const previousHp = this.hp;
    const nextHp = Math.max(0, previousHp - appliedDamage);

    this.hp = nextHp;
    logger.core.info(`Character ${this.characterId} took ${appliedDamage} damage, HP: ${nextHp}/${this.maxHp}`);

    // Sync with XState actor
    if (this.xstateActor) {
      this.xstateActor.send({ 
        type: 'DAMAGE_RECEIVED', 
        amount: appliedDamage,
        sourceId: details?.metadata?.sourceId as string | undefined
      });
    }

    const killed = nextHp <= 0;
    // Note: isDead state is managed by XState via DAMAGE_RECEIVED event
    // The health region transitions to 'dead' state which triggers triggerDeath action

    this.dispatchLifecycleEvent({
      characterId: this.characterId,
      configId: this.characterConfig.id,
      previousHealth: previousHp,
      currentHealth: nextHp,
      maxHealth: this.maxHp,
      delta: nextHp - previousHp,
      cause: 'damage',
      damageContext: details?.damageContext,
      metadata: details?.metadata
    });

    return killed;
  }
  
  /**
   * Heal this character.
   * @param amount Heal amount (positive number)
   * @param metadata Optional metadata forwarded to lifecycle scripts
   */
  public heal(amount: number, metadata?: Record<string, unknown>): void {
    const healAmount = Math.max(0, amount);
    const previousHp = this.hp;
    const nextHp = Math.min(this.maxHp, previousHp + healAmount);

    this.hp = nextHp;
    // Note: isDead state is managed by XState - healing a dead character requires RESPAWN event

    // Sync with XState actor
    if (this.xstateActor) {
      this.xstateActor.send({ 
        type: 'HEAL', 
        amount: healAmount 
      });
    }

    logger.core.debug(`Character ${this.characterId} healed ${healAmount}, HP: ${nextHp}/${this.maxHp}`);

    this.dispatchLifecycleEvent({
      characterId: this.characterId,
      configId: this.characterConfig.id,
      previousHealth: previousHp,
      currentHealth: nextHp,
      maxHealth: this.maxHp,
      delta: nextHp - previousHp,
      cause: 'heal',
      metadata
    });
  }

  private dispatchLifecycleEvent(event: CharacterHealthChangeEvent): void {
    if (this.lifecycleScripts.length === 0) {
      return;
    }

    const actions = runLifecycleScripts(this.lifecycleScripts, event);
    if (actions.length === 0) {
      return;
    }

    this.handleLifecycleActions(actions);
  }

  private handleLifecycleActions(actions: CharacterLifecycleAction[]): void {
    console.log(`[Character] handleLifecycleActions: ${actions.length} action(s):`, actions.map(a => a.type).join(', '));
    
    for (const action of actions) {
      switch (action.type) {
        case 'play-hit':
          this.playLifecycleHit(action.animation);
          break;
        case 'play-death':
          this.playLifecycleDeath(action.animation);
          break;
        case 'despawn':
          this.queueLifecycleDespawn(action.mode, action.delaySeconds ?? 0);
          break;
      }
    }
  }

  private playLifecycleHit(animation: string): void {
    if (!animation || this.getIsDead()) {
      return;
    }

    if (!this.animationController) {
      return;
    }

    // Set stagger flag to pause movement while hit animation plays
    this.hitStaggered = true;

    this.animationController.playOneShot(animation, () => {
      // Clear stagger flag
      this.hitStaggered = false;
      
      // Reset lastMovementInput to force edge detection on next frame
      // This ensures INPUT_MOVE/INPUT_STOP is sent based on whatever
      // the AI decides (walking or stopping)
      this.lastMovementInput = NaN;
      
      // Also send SYNC_LOCOMOTION as fallback to ensure animation plays
      // even if AI doesn't change state
      if (this.xstateActor) {
        StateManager.getInstance().sendCharacterEvent(this.characterId, { type: 'SYNC_LOCOMOTION' });
      }
    });
  }

  private playLifecycleDeath(animation?: string): void {
    console.log(`[Character] playLifecycleDeath called: animation=${animation}, alreadyPlaying=${this.lifecycleDeathAnimationPlaying}`);
    
    if (this.lifecycleDeathAnimationPlaying) {
      return;
    }

    this.lifecycleDeathAnimationPlaying = true;
    const clip = animation ?? 'death';

    console.log(`[Character] Playing death animation: ${clip}, hasAnimController=${!!this.animationController}`);
    
    if (this.animationController) {
      this.animationController.playOneShot(clip, () => this.onLifecycleDeathAnimationComplete());
    } else {
      this.onLifecycleDeathAnimationComplete();
    }
  }

  private onLifecycleDeathAnimationComplete(): void {
    this.lifecycleDeathAnimationPlaying = false;
    if (this.pendingDespawn && this.pendingDespawn.awaitingAnimation) {
      this.pendingDespawn.awaitingAnimation = false;
    }
  }

  private queueLifecycleDespawn(mode: 'immediate' | 'after-animation', delaySeconds: number = 0): void {
    const normalizedDelay = Math.max(0, delaySeconds);
    const awaitingAnimation = mode === 'after-animation' && this.lifecycleDeathAnimationPlaying;

    this.pendingDespawn = {
      mode,
      delaySeconds: normalizedDelay,
      awaitingAnimation
    };

    if (!awaitingAnimation && normalizedDelay === 0) {
      this.resolveLifecycleDespawn();
    }
  }

  private updateLifecycleDespawn(deltaTime: number): void {
    if (!this.pendingDespawn) {
      return;
    }

    if (this.pendingDespawn.awaitingAnimation) {
      if (this.lifecycleDeathAnimationPlaying) {
        return;
      }
      this.pendingDespawn.awaitingAnimation = false;
    }

    if (this.pendingDespawn.delaySeconds > 0) {
      this.pendingDespawn.delaySeconds = Math.max(0, this.pendingDespawn.delaySeconds - deltaTime);
      if (this.pendingDespawn.delaySeconds > 0) {
        return;
      }
    }

    this.resolveLifecycleDespawn();
  }

  public consumeDespawnRequest(): boolean {
    if (!this.pendingDespawnReady) {
      return false;
    }

    this.pendingDespawnReady = false;
    return true;
  }

  public hasLifecycleScripts(): boolean {
    return this.lifecycleScripts.length > 0;
  }

  private resolveLifecycleDespawn(): void {
    if (!this.pendingDespawn) {
      return;
    }

    this.pendingDespawnReady = true;
    this.pendingDespawn = null;
    // Actual despawn handling is performed by higher-level systems.
  }
  
  /**
   * Set the facing direction along the spline and immediately update rotation.
   * @param direction 1 for positive T direction, -1 for negative
   */
  public setFacingDirection(direction: number): void {
    this.lastMovementDirection = direction >= 0 ? 1 : -1;
    
    // Immediately update rotation to reflect the new facing direction
    // This is needed when setting facing outside of the normal update loop (e.g., after swap)
    const { projection } = this.splineConstraint.project(this.character.position, this.lastClosestIndex);
    const tangent = projection.tangent;
    const facingTangent = this.lastMovementDirection >= 0 ? tangent : tangent.negate();
    const targetAngle = Math.atan2(facingTangent.x, facingTangent.z);

    // Apply rotation on mesh (authoritative), then sync physics body
    if (!this.animationController?.isFacingLocked()) {
      this.character.rotation.y = targetAngle;
      this.syncPhysicsBodyToMesh();
    }
  }
  
  /**
   * Get the total length of the gameplay spline in meters.
   */
  public getSplineLength(): number {
    return this.splineConstraint.getTotalLength();
  }
  
  // Model orientation offset
  protected rotationOffset: number = 0;
  
  // Speed matching
  protected previousPosition: Vector3 = new Vector3(0, 0, 0);
  protected actualSpeed: number = 0;
  protected speedMultiplier: number = 1.0; // Can be overridden by subclasses (e.g., NPCs)
  // Spline constraint mode
  protected readonly USE_SPLINE_CONSTRAINT = true; // Enable spline movement for all characters

  // Physics tuning
  // jumpHeight is the desired apex height; gravity can be overridden per-character.
  protected readonly jumpHeight: number;
  protected readonly gravityOverride: number | null;

  constructor(
    scene: Scene,
    splineConstraint: SplineConstraint,
    characterConfig: CharacterConfig,
    protected characterId: string,
    protected isPlayer: boolean = false,
    physicsManager?: PhysicsManager
  ) {
    this.scene = scene;
    this.splineConstraint = splineConstraint;
    this.characterConfig = characterConfig;
    this.physicsManager = physicsManager || null;
    this.rotationOffset = characterConfig.rotationOffset ?? 0;
    this.damageCallback = characterConfig.onDamageReceived;
    this.attackStartCallback = characterConfig.onAttackStart;
    this.attackHitCallback = characterConfig.onAttackHit;
    this.hitReactionCallback = characterConfig.onHitReaction;
    this.lifecycleScripts = characterConfig.lifecycle ? [...characterConfig.lifecycle] : [];

    const gravityFromConfig = this.characterConfig.physics?.gravity;
    const jumpHeightFromConfig = this.characterConfig.physics?.jumpHeight;

    // Defaults match the previous hardcoded values: v=16, g=20 => h≈6.4
    this.jumpHeight = typeof jumpHeightFromConfig === 'number' ? Math.max(0, jumpHeightFromConfig) : 6.4;
    this.gravityOverride = typeof gravityFromConfig === 'number' ? Math.abs(gravityFromConfig) : null;

    // Initialize HP from config (default 100)
    this.maxHp = this.characterConfig.health?.max ?? 100;
    // Starting HP defaults to max if not specified
    this.hp = this.characterConfig.health?.start ?? this.maxHp;
    // Note: isDead is derived from XState health state, not stored as a boolean

    logger.character.debug(`Character constructor: ${characterId}, isPlayer=${isPlayer}`);

    // Create character capsule immediately
    this.character = MeshBuilder.CreateCapsule(`character_${characterId}`, {
      radius: 0.3,
      height: 1.8,
      tessellation: 16
    }, this.scene);
    this.character.position = new Vector3(0, 1.0, 0);
    
    const characterMaterial = new StandardMaterial(`characterMat_${characterId}`, this.scene);
    characterMaterial.diffuseColor = new Color3(1, 0.5, 0);
    characterMaterial.alpha = 0.5;
    this.character.material = characterMaterial;
    this.character.visibility = 0.0; // Hidden by default
    
    // Mark capsule as debug mesh - exclude from shadows
    this.character.receiveShadows = false;
    this.character.metadata = { isDebug: true, noShadows: true };

    // Debug rendering will be created after character is loaded
    
    this.character.checkCollisions = true; // Enable collisions
    this.character.ellipsoid = new Vector3(0.3, 0.9, 0.3);
    
    // Create physics body if physics manager available
    if (this.physicsManager && this.physicsManager.isReady()) {
      this.createPhysicsBody();
    }
    
    // Initialize previous position for speed tracking
    this.previousPosition = this.character.position.clone();
  }

  public getDamageCallback(): CharacterConfig['onDamageReceived'] | undefined {
    return this.damageCallback;
  }

  public setDamageCallback(callback: CharacterConfig['onDamageReceived'] | undefined): void {
    this.damageCallback = callback;
  }

  public getAttackStartCallback(): CharacterConfig['onAttackStart'] | undefined {
    return this.attackStartCallback;
  }

  public getAttackHitCallback(): CharacterConfig['onAttackHit'] | undefined {
    return this.attackHitCallback;
  }

  public getHitReactionCallback(): CharacterConfig['onHitReaction'] | undefined {
    return this.hitReactionCallback;
  }

  /**
   * Returns true if character is staggered from a hit reaction.
   * Movement should be paused while staggered.
   */
  public isHitStaggered(): boolean {
    return this.hitStaggered;
  }

  public setIsPlayerControlled(isPlayer: boolean): void {
    this.isPlayer = isPlayer;
    // XState doesn't need transition checker updates - it handles both player and NPC input
    
    // Update EntityRegistry so getPlayer() returns the correct character
    if (isPlayer) {
      EntityRegistry.getInstance().setCurrentPlayer(this.characterId);
    }
  }

  /**
   * Create kinematic physics body for character collision
   */
  private createPhysicsBody(): void {
    if (!this.physicsManager) return;
    
    try {
      // Phase 7: Use dynamic impostor with CHARACTER collision group
      // All characters (player + NPCs) use motor-driven velocity for proper collision response
      const { collisionMask, collisionGroup } = this.getCollisionFilter();
      
      const physicsBodyId = `character_physics_${this.characterId}`;

      this.physicsBody = this.physicsManager.createCharacterCapsule(
        physicsBodyId,
        this.character,
        70.0, // Mass (kg)
        0.5,  // Friction
        collisionMask,
        collisionGroup
      );
      
      // CRITICAL: Disable physics engine gravity on character bodies!
      // Characters use manual gravity calculation in updatePhysics() via _verticalVelocity.
      // If we don't disable per-body gravity, we get DOUBLE gravity (manual + physics engine).
      this.physicsBody.setBodyGravity(Vector3.Zero());
      
      // CRITICAL: BabylonJS PhysicsImpostor sets rotationQuaternion on the mesh,
      // which overrides Euler rotation.y. We need to clear it so that 
      // this.character.rotation.y works correctly.
      this.character.rotationQuaternion = null;
      
      // Lock rotation on X and Z axes (keep character upright)
      // Angular velocity will be constrained in updatePhysics
      
      logger.physics.debug(`Created dynamic physics body for character: ${this.characterId} (mass: 70kg, group: CHARACTER)`);
      
      // Register with EntityRegistry for type-safe collision lookups
      // Determine subtype based on config (TODO: should come from CharacterConfig)
      const subtype = this.isPlayer 
        ? CharacterSubtype.PLAYER 
        : CharacterSubtype.PARTY_MEMBER; // Default NPCs to party members
      
      EntityRegistry.getInstance().registerEntityInfo({
        id: this.characterId,
        physicsBodyId,
        type: EntityType.CHARACTER,
        characterType: this.characterConfig.name,
        characterSubtype: subtype,
        isPlayer: this.isPlayer,
        mesh: this.character,
        displayName: this.characterConfig.name,
      });
      
      logger.core.debug(`Registered character ${this.characterId} with EntityRegistry`);
    } catch (error) {
      logger.userError(`Failed to create physics body for ${this.characterId}:`, error);
    }
  }

  /**
   * Recompute collision filters based on current config flags.
   */
  private getCollisionFilter(): { collisionMask: number; collisionGroup: number } {
    const collisionMask = this.characterConfig.collidesWithCharacters === false
      ? (CollisionGroups.STATIC | CollisionGroups.DYNAMIC) // collide with environment/dynamics, not characters
      : CollisionMasks.CHARACTER;
    const collisionGroup = CollisionGroups.CHARACTER;
    return { collisionMask, collisionGroup };
  }

  /**
   * Update collision filtering at runtime.
   */
  public setCollidesWithCharacters(enabled: boolean): void {
    this.characterConfig.collidesWithCharacters = enabled;
    const { collisionMask, collisionGroup } = this.getCollisionFilter();
    if (this.physicsManager && this.physicsBody) {
      this.physicsManager.setCollisionFilter(
        `character_physics_${this.characterId}`,
        collisionGroup,
        collisionMask
      );
    }
  }

  public getCharacterConfig(): CharacterConfig {
    return this.characterConfig;
  }

  /**
   * Load character with a pre-loaded asset (for fast switching)
   */
  public loadFromAsset(asset: CharacterAsset): void {
    logger.character.log(`[LOAD FROM ASSET] ${this.characterId} - CALLED AT START! Asset:`, asset);
    logger.character.log(`Loading character from cache: ${this.characterConfig.name} (${this.characterId})`);
    
    this.characterAsset = asset;

    // Create visual root for rotation handling
    this.characterVisualRoot = new TransformNode(`characterVisualRoot_${this.characterId}`, this.scene);
    this.characterVisualRoot.parent = this.character;
    this.characterVisualRoot.position = new Vector3(0, -0.9, 0); // TODO, magic number offset?
    
    // Parent the character model to the visual root
    this.characterAsset.rootMesh.parent = this.characterVisualRoot;
    
    // Enable and make visible
    this.characterAsset.rootMesh.setEnabled(true);
    this.characterAsset.rootMesh.isVisible = true;
    
    // Scale applies to the mesh itself
    this.characterAsset.rootMesh.scaling.scaleInPlace(this.characterConfig.scale);
    
    // Build a set of embedded entity template mesh names to keep hidden
    const embeddedEntityMeshNames = new Set<string>();
    if (this.characterAsset.embeddedEntities) {
      for (const entity of this.characterAsset.embeddedEntities.entities.values()) {
        if (entity.type === 'projectile' || entity.type === 'weapon') {
          for (const mesh of entity.meshes) {
            embeddedEntityMeshNames.add(mesh.name);
          }
        }
      }
    }
    
    // Make all child meshes visible and enabled (except embedded entity templates)
    this.characterAsset.meshes.forEach((mesh) => {
      // Skip embedded entity template meshes - they're cloned separately when spawned
      // The cloned mesh name is `${originalName}_instance` so we check if name starts with
      // any embedded entity mesh name (handles both original and cloned names)
      let isEmbeddedTemplate = false;
      for (const embeddedName of embeddedEntityMeshNames) {
        if (mesh.name === embeddedName || mesh.name === `${embeddedName}_instance`) {
          isEmbeddedTemplate = true;
          break;
        }
      }
      if (isEmbeddedTemplate) {
        return;
      }
      mesh.setEnabled(true);
      mesh.isVisible = true;
      mesh.visibility = 1.0;
      if (mesh.material) {
        mesh.material.alpha = 1.0;
      }
    });
    
    // Ensure rootMesh is visible
    this.characterAsset.rootMesh.setEnabled(true);
    this.characterAsset.rootMesh.isVisible = true;
    this.characterAsset.rootMesh.visibility = 1.0;

    // Enable and make visible all submeshes (weapons, etc.)
    // CRITICAL: Re-attach submesh anchors to bones now that rootMesh is properly parented!
    // The attachToBone() in CharacterAssetCache was called before rootMesh was parented to
    // characterVisualRoot, so the bone world matrices weren't correct.
    this.characterAsset.submeshes.forEach((submesh, key) => {
      logger.character.debug(`Processing submesh '${key}': rootMesh=${submesh.rootMesh?.name}`);
      if (submesh.rootMesh) {
        // Check if submesh needs bone attachment
        const anchor = submesh.rootMesh.parent;
        if (submesh.attachBone && anchor && this.characterAsset?.skeleton) {
          const bone = this.characterAsset.skeleton.bones.find(b => b.name === submesh.attachBone);
          if (bone) {
            // Re-call attachToBone now that rootMesh is properly parented
            (anchor as TransformNode).attachToBone(bone, this.characterAsset.rootMesh);
          } else {
            logger.character.warn(`Bone '${submesh.attachBone}' not found in skeleton`);
          }
        }
        
        submesh.rootMesh.setEnabled(true);
        submesh.rootMesh.isVisible = true;
        submesh.rootMesh.visibility = 1.0;
        
        // Also enable all child meshes
        submesh.rootMesh.getChildMeshes?.()?.forEach((child: any) => {
          child.setEnabled(true);
          child.isVisible = true;
          child.visibility = 1.0;
        });
      }
    });

    // Initialize animation controller
    this.initAnimationController();

    // Initialize XState actor (primary state machine)
    this.initXStateActor();

    // NOTE: Debug meshes are NOT created by default - only when setDebugEnabled(true) is called
    // This avoids unnecessary mesh creation/updates for characters that never need debug visualization

    // Create physics body if it wasn't created during construction (physics may not have been ready yet)
    logger.physics.log(`[Physics Debug] ${this.characterId} - physicsBody exists: ${!!this.physicsBody}, physicsManager exists: ${!!this.physicsManager}, isReady: ${this.physicsManager?.isReady()}`);
    if (!this.physicsBody && this.physicsManager && this.physicsManager.isReady()) {
      logger.physics.log(`[Physics Debug] ${this.characterId} - Creating physics body in loadFromAsset`);
      this.createPhysicsBody();
    } else {
      logger.physics.log(`[Physics Debug] ${this.characterId} - Skipping physics body creation: physicsBody=${!!this.physicsBody}, manager=${!!this.physicsManager}, ready=${this.physicsManager?.isReady()}`);
    }

    logger.character.log(`Character loaded successfully from cache (${this.characterId})`);
  }

  /**
   * @deprecated Use loadFromAsset() with pre-cached assets from CharacterAssetCache.
   * This method bypasses the cache and loads fresh - only use for testing.
   * 
   * Fast switching architecture:
   * 1. CharacterAssetCache pre-loads all assets at startup
   * 2. Characters use loadFromAsset() with cached assets
   * 3. Switching = swap controller reference, no asset loading
   */
  public async load(): Promise<void> {
    logger.character.warn(`Character.load() bypasses cache - prefer CharacterAssetCache!`);
    try {
      const characterLoader = new CharacterLoader(this.scene);
      
      // Check if any attack uses embedded projectile references
      const hasEmbeddedProjectiles = this.characterConfig.combatAttacks 
        ? Object.values(this.characterConfig.combatAttacks).some(attack => 
            attack.projectile?.assetPath && isEmbeddedReference(attack.projectile.assetPath)
          )
        : false;
      
      const asset = await characterLoader.loadCharacter(
        this.characterConfig.id,
        this.characterConfig.assetPath,
        this.characterConfig.animations,
        this.characterConfig.modelAxes ?? this.characterConfig.rotationOffset,
        this.characterConfig.submeshes,
        this.characterConfig.renderFixes,
        hasEmbeddedProjectiles
      );

      this.loadFromAsset(asset);
    } catch (error) {
      logger.userError(`Failed to load character (${this.characterId}):`, error);
    }
  }

  protected initAnimationController() {
    if (!this.characterAsset) return;

    const submeshes = Array.from(this.characterAsset.submeshes.values());

    if (this.characterAsset.phaseMap.size > 0 && this.characterAsset.skeleton) {
      this.animationController = new AnimationController(
        this.scene, 
        this.characterAsset.skeleton, 
        this.characterAsset.phaseMap,
        this.characterConfig.transitions,
        this.characterId, // statePrefix
        this.characterAsset.rootMesh,
        submeshes,
        undefined, // eventBus
        this.characterId // characterId for logging
      );
    } else if (this.characterAsset.animations.size > 0 && this.characterAsset.skeleton) {
      // Fallback
      this.animationController = new AnimationController(
        this.scene,
        this.characterAsset.skeleton,
        Array.from(this.characterAsset.animations.values()),
        this.characterConfig.transitions || [],
        this.characterConfig.id, // statePrefix
        undefined,
        submeshes,
        undefined, // eventBus
        this.characterId // characterId for logging
      );
    }
  }

  /**
   * Initialize XState actor as the primary state machine.
   * Sprint 0024: XState is now the source of truth for character state.
   */
  protected initXStateActor(): void {
    if (!this.animationController) return;

    const stateManager = StateManager.getInstance();
    
    // Create XState actor with initial health
    this.xstateActor = stateManager.createCharacter(this.characterId, this.maxHp);
    
    // Register animation controller with action handler registry
    ActionHandlerRegistry.registerAnimationController(this.characterId, this.animationController);
    
    // Register character instance so action handlers can query current input state
    ActionHandlerRegistry.registerCharacter(this.characterId, this);
    
    // Register physics handler so XState actions can request jumps
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    registerPhysicsHandler(this.characterId, {
      requestJump: () => self.requestJump(),
      setDesiredMovement: (x: number, z: number) => self.setDesiredMovement(new Vector3(x, 0, z)),
      getIsGrounded: () => self.isGrounded,
    });
    
    logger.core.log(`[Character] XState actor initialized for ${this.characterId} (primary mode)`);
  }

  public update(deltaTime: number): void {
    if (!this.character || !this.xstateActor) return;

    // 0. Calculate Actual Speed (before movement)
    this.updateActualSpeed(deltaTime);

    // 1. Process input and send XState events
    this.processInputEvents();

    // 2. Check ground BEFORE applying physics (so gravity knows if we're grounded)
    this.checkGroundCollision(deltaTime);
    
    // 3. Send physics state events to XState
    this.processPhysicsEvents();

    // 4. Update Physics (Gravity) - AFTER ground check
    this.updatePhysics(deltaTime);

    // 5. Calculate movement based on XState locomotion state
    this.calculateMovementFromState();

    // 6. Apply Movement (Collision & Spline Constraint)
    this.applyMovement(deltaTime);

    // 7. Update Animation (includes speed matching)
    this.updateAnimation(deltaTime);
    
    // 8. Sync mesh FROM physics (allows physics-driven pushing by dynamic objects)
    this.syncMeshFromPhysicsBody();
    
    // 9. Update debug visualization
    this.updateCharacterDebugMeshes();

    this.updateLifecycleDespawn(deltaTime);
  }
  
  /**
   * Process input and send appropriate XState events.
   * This replaces the legacy state machine's input handling.
   */
  protected processInputEvents(): void {
    if (!this.xstateActor) return;
    
    const stateManager = StateManager.getInstance();
    const movementInput = this.getInputDirection();
    const jumpInput = this.isJumpPressed();
    
    // DEBUG: Log input every frame for player
    if (this.characterId === 'daniel_party_0' && (movementInput !== 0 || jumpInput)) {
      logger.input.log(`[INPUT DEBUG] ${this.characterId}: movement=${movementInput}, jump=${jumpInput}, lastMovement=${this.lastMovementInput}`);
    }
    
    // Get current XState locomotion state
    const snapshot = this.xstateActor.getSnapshot();
    const locomotionState = (snapshot.value as { locomotion?: string })?.locomotion;
    
    // Movement input changes (edge detection)
    if (movementInput !== this.lastMovementInput) {
      if (movementInput !== 0) {
        logger.character.log(`[Character] ${this.characterId} sending INPUT_MOVE direction=${movementInput}`);
        stateManager.sendCharacterEvent(this.characterId, { 
          type: 'INPUT_MOVE', 
          direction: movementInput 
        });
      } else {
        logger.character.log(`[Character] ${this.characterId} sending INPUT_STOP`);
        stateManager.sendCharacterEvent(this.characterId, { type: 'INPUT_STOP' });
      }
      this.lastMovementInput = movementInput;
    }
    
    // Jump input (on press, not hold)
    if (jumpInput && !this.lastJumpInput) {
      if (this.isGrounded && locomotionState !== 'jumping') {
        stateManager.sendCharacterEvent(this.characterId, { type: 'INPUT_JUMP' });
      }
    }
    this.lastJumpInput = jumpInput;
  }
  
  /**
   * Send physics state events to XState (grounded/airborne transitions).
   */
  protected processPhysicsEvents(): void {
    if (!this.xstateActor) return;
    
    const stateManager = StateManager.getInstance();
    const snapshot = this.xstateActor.getSnapshot();
    const locomotionState = (snapshot.value as { locomotion?: string })?.locomotion;
    
    // Handle airborne detection
    if (!this.isGrounded && this.velocity.y < -0.5) {
      if (locomotionState === 'idle' || locomotionState === 'walking' || locomotionState === 'jumping') {
        stateManager.sendCharacterEvent(this.characterId, { type: 'PHYSICS_AIRBORNE' });
      }
    }
    
    // Handle landing detection
    if (this.isGrounded) {
      if (locomotionState === 'falling' || locomotionState === 'jumping') {
        stateManager.sendCharacterEvent(this.characterId, { type: 'PHYSICS_LANDED' });
        // Immediately check if we should be moving or idle
        const movementInput = this.getInputDirection();
        if (movementInput !== 0) {
          stateManager.sendCharacterEvent(this.characterId, { 
            type: 'INPUT_MOVE', 
            direction: movementInput 
          });
        }
      }
    }
  }
  
  /**
   * Calculate desired movement based on current XState locomotion state.
   * This replaces the per-state handleMovement() methods.
   */
  protected calculateMovementFromState(): void {
    if (!this.xstateActor) return;
    
    const snapshot = this.xstateActor.getSnapshot();
    const locomotionState = (snapshot.value as { locomotion?: string })?.locomotion;
    const context = snapshot.context;
    
    // Check for channeled attack with lockMovement
    const activeAttack = context?.activeAttack;
    const isMovementLocked = activeAttack?.attackDef?.channeled?.lockMovement === true;
    
    // Determine if we should move based on locomotion state
    const canMove = locomotionState === 'walking' || locomotionState === 'jumping' || locomotionState === 'falling';
    
    if (!canMove || isMovementLocked) {
      this.desiredMovement.set(0, 0, 0);
      return;
    }
    
    // Calculate movement direction from input
    const movementInput = this.getInputDirection();
    if (movementInput === 0) {
      this.desiredMovement.set(0, 0, 0);
      return;
    }
    
    // Get camera-relative movement direction
    const camRight = this.getCameraRight();
    const flatRight = new Vector3(camRight.x, 0, camRight.z).normalize();
    const moveDir = flatRight.scale(movementInput);
    
    if (moveDir.lengthSquared() > 0) {
      moveDir.normalize();
    }
    
    // Apply movement speed
    const speed = this.characterConfig.moveSpeed || 5;
    const movement = moveDir.scale(speed);
    
    this.desiredMovement.copyFrom(movement);
  }
  
  /**
   * Get camera right vector for movement calculation.
   * Override in PlayerController with actual camera reference.
   */
  protected getCameraRight(): Vector3 {
    // Default: assume camera is looking down Z axis
    return new Vector3(1, 0, 0);
  }
  
  /**
   * Check if jump is currently pressed.
   * Override in subclasses with actual input.
   */
  protected isJumpPressed(): boolean {
    return false;
  }
  
  /**
   * Get current input direction. Override in subclasses.
   * Made public so ActionHandlers can query current input state.
   */
  public getInputDirection(): number {
    // Override in PlayerController to return actual input
    return 0;
  }

  protected updatePhysics(deltaTime: number) {
    // Use isolated _verticalVelocity to prevent any external corruption

    // Gravity source of truth (priority order):
    // 1. Zone-based gravity override (from trigger zones via ServiceRegistry)
    // 2. Character config gravity override
    // 3. World/map gravity from PhysicsManager
    // 4. Legacy default
    let effectiveGravity: number;
    
    // Check for zone-based gravity override first
    const zoneGravity = ServiceRegistry.isInitialized() 
      ? ServiceRegistry.getEntityGravityOverride(this.getId())
      : undefined;
    
    // Track if we're using zone gravity (needs different application logic)
    let usingZoneGravity = false;
    
    if (zoneGravity) {
      // Zone provides full Vector3 gravity - use Y component directly
      // Positive Y = upward force, negative Y = downward force
      effectiveGravity = zoneGravity.y;
      usingZoneGravity = true;
      // Apply horizontal gravity components if present
      if (Math.abs(zoneGravity.x) > 0.001 || Math.abs(zoneGravity.z) > 0.001) {
        // Add horizontal gravity force to velocity
        this.velocity.x += zoneGravity.x * deltaTime;
        this.velocity.z += zoneGravity.z * deltaTime;
      }
    } else if (this.gravityOverride !== null) {
      effectiveGravity = this.gravityOverride;
    } else {
      const worldGravityY = this.physicsManager ? this.physicsManager.getGravity().y : undefined;
      effectiveGravity = typeof worldGravityY === 'number' ? Math.abs(worldGravityY) : 20.0;
    }
    
    // For jump calculations, we need absolute gravity magnitude
    const gravityMagnitude = Math.abs(effectiveGravity);
    const jumpVelocity = Math.sqrt(2 * gravityMagnitude * this.jumpHeight);
    
    // 1. Apply jump impulse FIRST
    if (this.jumpRequested) {
      this._verticalVelocity = jumpVelocity;
      this.jumpRequested = false;
      this.isGrounded = false; // Force airborne when jumping
      // CRITICAL: Clear lastKnownGroundY when jumping so the fallthrough protection
      // doesn't snap the character back to their jump origin height.
      // This allows jumping to lower platforms without getting stuck.
      this.lastKnownGroundY = null;
    }
    // 2. Apply gravity if airborne
    else if (!this.isGrounded) {
      if (usingZoneGravity) {
        // Zone gravity: positive Y = upward force, negative Y = downward force
        // ADD the gravity value to velocity (so +10 pushes up, -10 pulls down)
        this._verticalVelocity += effectiveGravity * deltaTime;
      } else {
        // Normal gravity: always pulls down (effectiveGravity is positive magnitude)
        this._verticalVelocity -= effectiveGravity * deltaTime;
      }
    } 
    // 3. On ground - handle grounded state
    else {
      // If zone gravity is pushing upward, lift off the ground!
      if (usingZoneGravity && effectiveGravity > 0) {
        this.isGrounded = false;
        this._verticalVelocity = effectiveGravity * deltaTime;
      } else {
        this._verticalVelocity = 0;
      }
    }

    // Sync to public velocity for state transitions to read
    this.velocity.y = this._verticalVelocity;
    
    // 4. Integrate desired horizontal movement into velocity
    // For spline-constrained movement, project input onto tangent direction
    if (this.USE_SPLINE_CONSTRAINT && this.splineConstraint) {
      const { projection } = this.splineConstraint.project(this.character.position, this.lastClosestIndex);
      const tangent = projection.tangent.normalize();
      
      // Get input magnitude and direction
      const desiredFlat = new Vector3(this.desiredMovement.x, 0, this.desiredMovement.z);
      const desiredSpeed = desiredFlat.length();
      
      if (desiredSpeed > 0.01) {
        // Determine if input wants to go forward or backward along tangent
        const inputDir = desiredFlat.normalize();
        const alignment = Vector3.Dot(inputDir, tangent);
        const direction = Math.sign(alignment);
        
        // Move at full input speed along tangent in determined direction
        this.velocity.x = tangent.x * desiredSpeed * direction;
        this.velocity.z = tangent.z * desiredSpeed * direction;
      } else {
        this.velocity.x = 0;
        this.velocity.z = 0;
      }
    } else {
      // Free movement - use desired movement directly
      this.velocity.x = this.desiredMovement.x;
      this.velocity.z = this.desiredMovement.z;
    }
    
    // === PATH CONSTRAINT FORCES (Phase 3: Force-based path attraction) ===
    // Apply spring-damper forces to pull character toward spline path (XZ only)
    // Applied AFTER desiredMovement so it acts as a corrective force
    if (this.USE_SPLINE_CONSTRAINT && this.characterConfig.pathConstraint?.enabled && this.splineConstraint) {
      const stiffness = this.characterConfig.pathConstraint.stiffness ?? 100;
      const damping = this.characterConfig.pathConstraint.damping ?? 10;
      const attractionGain = this.characterConfig.pathConstraint.attractionGain ?? 2.0; // Stronger lateral pull toward path
      
      // Get current velocity (isolated primitive values to avoid corruption)
      const currentVelocity = new Vector3(this.velocity.x, this.velocity.y, this.velocity.z);
      
      // Apply path constraint forces
      const { force, closestIndex } = this.splineConstraint.applyPathForces(
        this.character.position,
        currentVelocity,
        stiffness,
        damping,
        this.lastClosestIndex
      );
      
      this.lastClosestIndex = closestIndex;
      
      // Integrate force into velocity (F = ma, assuming unit mass)
      // Force is in world units/sec^2, so multiply by deltaTime for velocity change
      // This acts as a corrective force pulling character back to path
      this.velocity.x += force.x * attractionGain * deltaTime;
      this.velocity.z += force.z * attractionGain * deltaTime;
      
      // Optional: Constrain vertical if configured
      if (this.characterConfig.pathConstraint.constrainVertical) {
        // Disable vertical forces while using motor physics to avoid slow drift; rely on grounding instead
        // this.velocity.y += force.y * deltaTime;
      }
    }

    // When grounded, zero out vertical velocity and stick to last known ground height to prevent drift
    if (this.isGrounded) {
      this.velocity.y = 0;
      this._verticalVelocity = 0;
      if (this.lastKnownGroundY !== null) {
        this.character.position.y = this.lastKnownGroundY;
      }
    }
  }

  protected checkGroundCollision(deltaTime: number): void {
    // Decrease grace time every frame
    this.groundedGraceRemainingSeconds = Math.max(0, this.groundedGraceRemainingSeconds - deltaTime);

    const wasGrounded = this.isGrounded;

    // === PHYSICS RAYCAST: Authoritative ground detection ===
    // Use the physics engine's native raycast against static geometry.
    // This works regardless of body activation state (unlike contact manifolds).
    const rayStart = this.character.position.clone();
    const rayEnd = rayStart.clone();
    rayEnd.y -= 1.4; // Ray length: capsule height (1.8) * 0.75

    // Raycast against STATIC collision group only (0x01)
    const hit = this.physicsManager?.raycast(rayStart, rayEnd, 0x01);

    if (hit && hit.hitNormal.y > 0.5 && this._verticalVelocity <= 0.1) {
      // Valid ground hit: normal points upward and we're not moving up
      const groundY = hit.hitPoint.y + 0.9; // Ground surface + half capsule height

      this.character.position.y = groundY;
      this.isGrounded = true;
      this.groundedGraceRemainingSeconds = this.GROUNDED_GRACE_TIME_SECONDS;
      this.lastKnownGroundY = groundY;
    } else {
      // No ground hit - check grace period
      if (this.groundedGraceRemainingSeconds > 0 && this._verticalVelocity <= 0.1) {
        this.isGrounded = true;
      } else {
        this.isGrounded = false;
      }
    }

    // CRITICAL: When transitioning from grounded to airborne, clear lastKnownGroundY.
    // This allows characters to intentionally fall off cliffs/platforms without
    // the "fallthrough protection" snapping them back to their previous ground height.
    if (wasGrounded && !this.isGrounded) {
      this.lastKnownGroundY = null;
    }

    // Safety: if we have a valid lastKnownGroundY and we dip below it, snap back.
    // This prevents tunneling through floors.
    if (!this.isGrounded && this.lastKnownGroundY !== null) {
      const fallthroughDepth = this.lastKnownGroundY - this.FALLTHROUGH_SNAP_THRESHOLD;
      if (this.character.position.y < fallthroughDepth) {
        this.character.position.y = this.lastKnownGroundY;
        this.velocity.y = 0;
        this._verticalVelocity = 0;
        this.isGrounded = true;
        this.groundedGraceRemainingSeconds = this.GROUNDED_GRACE_TIME_SECONDS;
      }
    }
  }
  
  protected updateActualSpeed(deltaTime: number) {
    if (deltaTime <= 0) return;
    
    // Calculate horizontal movement delta
    const currentPos = this.character.position;
    const delta = currentPos.subtract(this.previousPosition);
    delta.y = 0; // Only horizontal movement
    
    // Calculate speed in units/second
    this.actualSpeed = delta.length() / deltaTime;
    
    // Store current position for next frame
    this.previousPosition.copyFrom(currentPos);
  }

  protected updateAnimation(deltaTime: number) {
    if (!this.animationController) return;
    
    // Apply speed matching for locomotion animations
    const currentState = this.animationController.getCurrentState();
    this.applySpeedMatching(currentState);
    
    // Update animation blending
    this.animationController.update(deltaTime);
  }

  protected applySpeedMatching(_animationState: string) {
    if (!this.animationController) return;
    
    // Get the active animation group's metadata
    const activeGroup = (this.animationController as any).activeGroup;
    if (!activeGroup || !activeGroup.metadata) return;
    
    const naturalSpeed = activeGroup.metadata.naturalSpeed;
    if (naturalSpeed === undefined || naturalSpeed === null) return; // No speed matching for this animation
    
    // Calculate speed ratio
    const minSpeed = 0.5;
    const maxSpeed = 2.0;
    
    let speedRatio = 1.0;
    if (naturalSpeed > 0.01) {
      speedRatio = this.actualSpeed / naturalSpeed;
      speedRatio = Math.max(minSpeed, Math.min(maxSpeed, speedRatio));
    }
    
    // Apply default speed multiplier if set
    const defaultSpeed = activeGroup.metadata.defaultSpeed || 1.0;
    const finalSpeed = speedRatio * defaultSpeed;
    
    this.animationController.setSpeedRatio(finalSpeed);
  }

  protected applyMovement(deltaTime: number) {
    // Keep character capsule completely upright (no rotation at all)
    this.character.rotation.x = 0;
    this.character.rotation.z = 0;

    // Phase 7: Use motor-driven velocity instead of manual position updates
    if (this.physicsBody) {
      // Lock angular velocity (prevent tilting/spinning)
      this.physicsBody.setAngularVelocity(Vector3.Zero());

      // Ensure the body is awake before applying motor velocity (Ammo can ignore impulses when sleeping)
      this.physicsBody.setEnabled(true);
      
      // Apply motor velocity to physics engine
      // The velocity vector is in world-units per game-second.
      // Physics engine timestep is now controlled via setTimeStep(scaledDelta), 
      // so we pass velocity directly without additional scaling.
      this.physicsBody.setLinearVelocity(this.velocity);
      
      // Physics engine will automatically update this.character.position
      // No manual position.y += delta needed!
      
    } else {
      // Fallback: Manual position update if physics not available
      const verticalDelta = this._verticalVelocity * deltaTime;
      this.character.position.y += verticalDelta;
      
      // Manual horizontal movement for non-physics characters
      this.character.position.x += this.velocity.x * deltaTime;
      this.character.position.z += this.velocity.z * deltaTime;
    }

    if (this.USE_SPLINE_CONSTRAINT) {
      let needsPhysicsSync = false;

      // === SPLINE-CONSTRAINED MOVEMENT ===
      // Phase 7: With motor physics, position is updated by physics engine via setLinearVelocity()
      // Spline projection is only used for: (1) tracking T parameter, (2) rotation alignment
      
      const { projection, closestIndex: idx } = this.splineConstraint.project(this.character.position, this.lastClosestIndex);
      this.lastClosestIndex = idx;
      this.playerT = projection.t;

      // Snap XZ onto the spline to remove drift while preserving jump height
      const snapX = projection.splinePoint.x;
      const snapZ = projection.splinePoint.z;
      if (Math.abs(this.character.position.x - snapX) > 0.0005 || Math.abs(this.character.position.z - snapZ) > 0.0005) {
        this.character.position.x = snapX;
        this.character.position.z = snapZ;
        needsPhysicsSync = true;
      }
      
      const splineDirection = projection.tangent.clone().normalize();
      
      // Calculate movement direction along spline from current velocity
      const velocityFlat = new Vector3(this.velocity.x, 0, this.velocity.z);
      const dotProduct = Vector3.Dot(velocityFlat, splineDirection);
      const inputSpeed = velocityFlat.length();
      
      // Track movement direction for rotation
      let movementDirection = 0;
      if (inputSpeed > 0.01 && Math.abs(dotProduct) > 0.1) {
        movementDirection = Math.sign(dotProduct);
        this.lastMovementDirection = movementDirection;
      }
      
      // Allow facing changes based on input during channeled attacks with lockMovement but not lockFacing
      // This lets the character turn L/R while staying in place during the beam attack
      if (this.xstateActor) {
        const snapshot = this.xstateActor.getSnapshot();
        const activeAttack = snapshot.context?.activeAttack;
        const channeledConfig = activeAttack?.attackDef?.channeled;
        if (channeledConfig?.lockMovement && !channeledConfig?.lockFacing) {
          const inputDir = this.getInputDirection();
          if (inputDir !== 0) {
            this.lastMovementDirection = inputDir;
          }
        }
      }
      
      // ALWAYS update rotation to align with spline tangent
      {
        const effectiveDirection = movementDirection !== 0 ? movementDirection : this.lastMovementDirection;
        const facingTangent = effectiveDirection >= 0 ? splineDirection : splineDirection.negate();
        const targetAngle = Math.atan2(facingTangent.x, facingTangent.z);

        // Always update the intended facing direction (used by projectiles, etc.)
        // This is authoritative even when mesh rotation is locked during turn animations
        this.currentForwardDirection.copyFrom(facingTangent);

        if (!this.animationController?.isFacingLocked()) {
          this.character.rotation.y = targetAngle;
          needsPhysicsSync = true;
        }
      }

      if (needsPhysicsSync) {
        this.syncPhysicsBodyToMesh();
      }
      
    } else {
      // === FREE 3D MOVEMENT (No Spline Constraint) ===
      // Phase 7: With motor physics, just set rotation based on movement direction
      const velocityFlat = new Vector3(this.velocity.x, 0, this.velocity.z);
      const inputSpeed = velocityFlat.length();
      
      if (inputSpeed > 0.01) {
        // Rotate character to face movement direction
        const targetAngle = Math.atan2(velocityFlat.x, velocityFlat.z);
        this.character.rotation.y = targetAngle;
        
        // Update intended facing direction for projectiles etc.
        this.currentForwardDirection.copyFrom(velocityFlat.normalize());
      }
      // Position will be updated by physics engine via motor velocity
    }
    
    // No manual syncPhysicsBodyToMesh() call needed - physics engine updates mesh position
  }

  // Public API for States
  public setDesiredMovement(movement: Vector3) {
    // States express what movement they WANT
    // Physics will integrate this into velocity during updatePhysics
    this.desiredMovement.copyFrom(movement);
  }

  public requestJump() {
    // Request a jump impulse on next physics update
    this.jumpRequested = true;
  }

  protected enforceInPlaceAnimation() {
    if (!this.characterAsset || !this.characterAsset.skeleton) return;
    
    const skeleton = this.characterAsset.skeleton;
    
    // Use configured rootBoneName, or fall back to common naming conventions
    const configuredRootName = this.characterConfig.rootBoneName;
    const rootBone = configuredRootName
      ? skeleton.bones.find(b => b.name === configuredRootName)
      : skeleton.bones.find(b => b.name.includes('Hips') || b.name.includes('Root'));
    
    if (rootBone) {
      rootBone.position.x = 0;
      rootBone.position.z = 0;
    }
  }
  
  /**
   * Update the projection cache (lastClosestIndex) based on current position
   * without changing playerT. Useful when position/T are set externally.
   */
  public updateProjectionCache(): void {
    const { closestIndex } = this.splineConstraint.project(this.character.position, 0);
    this.lastClosestIndex = closestIndex;
  }
  
  public resetPhysics(): void {
    this.velocity = new Vector3(0, 0, 0);
    this._verticalVelocity = 0;
    this.isGrounded = false;
    
    // Ensure rotationQuaternion is cleared so Euler rotation.y works
    this.character.rotationQuaternion = null;
    
    // Update lastClosestIndex based on current playerT (if set) to help future projections
    // We estimate the index from T to give findClosestPoint a good starting point
    // This avoids the search radius problem when spawning far from index 0
    const estimatedIndex = Math.floor(this.playerT * this.splineConstraint.getSplinePointCount());
    const { projection, closestIndex } = this.splineConstraint.project(this.character.position, estimatedIndex);
    this.lastClosestIndex = closestIndex;
    // Don't overwrite playerT here - it was intentionally set by spawn code
    // The projection will naturally sync when the character moves
    
    // CRITICAL: Align character rotation to spline tangent
    // Without this, after swap/respawn the character faces wrong direction
    const tangent = projection.tangent;
    const facingTangent = this.lastMovementDirection >= 0 ? tangent : tangent.negate();
    const targetAngle = Math.atan2(facingTangent.x, facingTangent.z);
    
    // Apply rotation on mesh
    this.character.rotation.y = targetAngle;

    // Keep physics body aligned with the new transform
    this.syncPhysicsBodyToMesh();
    
    // Reset animation state via XState
    if (this.xstateActor) {
      const stateManager = StateManager.getInstance();
      stateManager.sendCharacterEvent(this.characterId, { type: 'INPUT_STOP' });
    }
  }

  public getCurrentAnimationState(): string {
    return this.animationController ? this.animationController.getCurrentState() : 'none';
  }

  public getAnimationDebugInfo(): string {
    return this.animationController ? this.animationController.getDebugInfo() : 'AnimationController: none';
  }

  public getMissingAnimations(): string[] {
    return this.animationController ? this.animationController.getMissingAnimations() : [];
  }

  /**
   * Get the current locomotion state from XState.
   */
  public getCurrentState(): string {
    if (!this.xstateActor) return 'NONE';
    const snapshot = this.xstateActor.getSnapshot();
    const locomotion = (snapshot.value as { locomotion?: string })?.locomotion;
    return locomotion?.toUpperCase() ?? 'NONE';
  }

  public setDebugMeshVisibility(visible: boolean): void {
    this.character.visibility = visible ? 1.0 : 0.0;
  }

  /**
   * Enable/disable character debug visualization.
   * When enabled, creates debug meshes and updates them each frame.
   * When disabled, cleans up debug meshes to save resources.
   */
  public setDebugEnabled(enabled: boolean): void {
    console.log(`[Character] setDebugEnabled(${enabled}) for ${this.characterId}, current=${this.debugEnabled}`);
    if (this.debugEnabled === enabled) return;
    
    this.debugEnabled = enabled;
    
    if (enabled) {
      // Create debug meshes on demand
      console.log(`[Character] Creating debug meshes for ${this.characterId}`);
      this.createCharacterDebugMeshes();
    } else {
      // Cleanup debug meshes to save resources
      this.cleanupCharacterDebugMeshes();
    }
  }

  /**
   * Check if debug visualization is enabled.
   */
  public isDebugEnabled(): boolean {
    return this.debugEnabled;
  }

  public setWeaponMountDebugVisibility(visible: boolean): void {
    if (!this.characterAsset) return;
    
    this.characterAsset.submeshes.forEach(submesh => {
      if (submesh.debugMesh) {
        // Set visibility (not isVisible) so they can still be toggled via layer mask
        submesh.debugMesh.visibility = visible ? 1.0 : 0.0;
      }
    });
  }

  public setPosition(position: Vector3): void {
    // Mesh authoritative
    this.character.position.copyFrom(position);
    this.previousPosition.copyFrom(position);

    this.syncPhysicsBodyToMesh();
  }
  
  /**
   * Sync the physics body position to the current mesh position.
   * This is needed because kinematic physics bodies don't automatically follow the mesh.
   */
  protected syncPhysicsBodyToMesh(): void {
    if (this.physicsManager) {
      const body = this.physicsManager.getBody(`character_physics_${this.characterId}`);
      if (body) {
        body.setPosition(this.character.position.clone());
        body.setRotation(this.character.rotation.clone());
      }
    }
  }

  /**
   * Sync mesh position FROM physics body.
   * Called after physics step to allow physics-driven corrections (e.g., being pushed by dynamic objects).
   */
  protected syncMeshFromPhysicsBody(): void {
    if (this.physicsManager) {
      const body = this.physicsManager.getBody(`character_physics_${this.characterId}`);
      if (body) {
        // Only sync position if physics body has moved significantly from mesh
        // (indicates external forces/pushing)
        const bodyPos = body.mesh.position;
        const distance = Vector3.Distance(bodyPos, this.character.position);
        
        if (distance > 0.01) {
          const verticalDrop = this.character.position.y - bodyPos.y;
          const maxAllowedDrop = this.FALLTHROUGH_SNAP_THRESHOLD + 0.1;

          // Ignore one-frame teleport spikes far below ground (seen when jumping)
          if (verticalDrop > maxAllowedDrop) {
            logger.physics.debug(`Character ${this.characterId} ignoring sudden drop from physics (${verticalDrop.toFixed(2)}m), snapping body back to mesh`);
            body.setPosition(this.character.position.clone());
            body.setLinearVelocity(Vector3.Zero());
            return;
          }

          // Physics pushed the character - accept it
          this.character.position.copyFrom(bodyPos);
          logger.physics.debug(`Character ${this.characterId} pushed by physics: ${distance.toFixed(3)}m`);
        }
      }
    }
  }

  public getPosition(): Vector3 {
    return this.character.position;
  }

  /**
   * Get the actual forward direction the character is facing
   */
  public getForwardDirection(): Vector3 {
    // Return the forward direction computed during last applyMovement
    return this.currentForwardDirection;
  }

  /**
   * Create character debug meshes
   */
  private createCharacterDebugMeshes(): void {
    const debugRenderer = DebugRenderer.getInstance();
    
    // Create facing arrow - RED for player, MAGENTA for NPC
    const color = this.isPlayer ? new Color3(1, 0, 0) : new Color3(1, 0, 1); // RED for player, MAGENTA for NPC
    const arrowOrigin = Vector3.Zero(); // Will be positioned relative to parent
    const forward = new Vector3(0, 0, 1); // Points forward in local space
    
    this.debugFacingArrow = debugRenderer.createArrow(arrowOrigin, forward, 1.0, color);

    // Parent to character capsule (stable physics root)
    this.debugFacingArrow.parent = this.character;
    this.debugFacingArrow.position = new Vector3(0, 0.1, 0); // Slightly above capsule center
    
    // Create facing label
    const facingLabelText = this.isPlayer ? "P-Facing" : "N-Facing";
    this.debugFacingLabel = debugRenderer.createTextLabel(facingLabelText, Vector3.Zero(), color, 36);
    // Parent label to the arrow so it moves with it
    this.debugFacingLabel.parent = this.debugFacingArrow;
    this.debugFacingLabel.position = new Vector3(0, 0.5, 0); // Above the arrow
    
    // Create Z-axis visualization (BRIGHT MAGENTA vertical line) - parented to character capsule
    // const zAxisStart = new Vector3(0, -0.9, 0);
    // const zAxisEnd = new Vector3(0, 2.0, 0);
    // this.debugZAxis = MeshBuilder.CreateLines('zAxis', {
    //   points: [zAxisStart, zAxisEnd],
    //   updatable: true
    // }, this.scene);
    // (this.debugZAxis as any).color = new Color3(1, 0, 1); // BRIGHT MAGENTA
    // this.debugZAxis.layerMask = 0x20000000; // Debug layer
    // this.debugZAxis.isPickable = false;
    // this.debugZAxis.checkCollisions = false;
    // this.debugZAxis.parent = this.character; // Parent to capsule
    
    // Create tangent arrow - YELLOW for player, GREEN for NPC (world space, not parented)
    const tangentColor = this.isPlayer ? new Color3(1, 1, 0) : new Color3(0, 1, 0); // YELLOW for player, GREEN for NPC
    this.debugTangentArrow = debugRenderer.createArrow(arrowOrigin, forward, 1.0, tangentColor);
    
    // Create tangent label - will be positioned in updateCharacterDebugMeshes
    const tangentLabelText = this.isPlayer ? "P-Tangent" : "N-Tangent";
    this.debugTangentLabel = debugRenderer.createTextLabel(tangentLabelText, Vector3.Zero(), tangentColor, 36);
    // Parent label to the tangent arrow so it moves with it
    this.debugTangentLabel.parent = this.debugTangentArrow;
    this.debugTangentLabel.position = new Vector3(0, 0.5, 0); // Above the arrow
  }

  /**
   * Update character debug meshes
   */
  private updateCharacterDebugMeshes(): void {
    if (!this.debugEnabled) return;
    if (!this.debugFacingArrow || !this.characterVisualRoot) return;
    
    // Facing arrow is parented to the root mesh - no manual updates needed!
    // It passively inherits the visual character transform.
    
    // Update tangent arrow - shows raw spline tangent direction (world space)
    if (this.debugTangentArrow) {
      this.debugTangentArrow.position.copyFrom(this.character.position);
      this.debugTangentArrow.position.y = this.character.position.y - 0.9; // At feet level
      
      // Get spline projection at current position
      const { projection } = this.splineConstraint.project(this.character.position, this.lastClosestIndex);
      const tangent = projection.tangent;
      
      // Calculate angle from tangent (horizontal only)
      const tangentAngle = Math.atan2(tangent.x, tangent.z);
      
      // Apply same rotation as facing arrow (tilt forward then rotate)
      const tiltForward = Quaternion.RotationAxis(Vector3.Right(), Math.PI / 2);
      const yRotation = Quaternion.RotationAxis(Vector3.Up(), tangentAngle);
      this.debugTangentArrow.rotationQuaternion = yRotation.multiply(tiltForward);
      
      // Calculate angle difference between visual facing and tangent.
      // Use the *actual visual forward* from the rendered root mesh so this works with arbitrary modelAxes.
      let visualFacingAngle = this.character.rotation.y;
      if (this.characterAsset?.rootMesh) {
        const worldMatrix = this.characterAsset.rootMesh.getWorldMatrix();
        const visualForward = Vector3.TransformNormal(Vector3.Forward(), worldMatrix).normalize();
        visualFacingAngle = Math.atan2(visualForward.x, visualForward.z);
      }
      let angleDiff = visualFacingAngle - tangentAngle;
      // Normalize to [-π, π]
      while (angleDiff > Math.PI) angleDiff -= 2 * Math.PI;
      while (angleDiff < -Math.PI) angleDiff += 2 * Math.PI;
      const angleDiffDegrees = Math.abs(angleDiff * 180 / Math.PI);
      
      // Update or create angle arc visualization
      // Check isDisposed() to avoid double-dispose errors
      if (this.debugAngleArc && !this.debugAngleArc.isDisposed()) {
        this.debugAngleArc.dispose();
      }
      this.debugAngleArc = null;
      
      // Create arc showing angle between visual facing and tangent
      const arcRadius = 0.8;
      const arcPoints: Vector3[] = [];
      const startAngle = Math.min(visualFacingAngle, tangentAngle);
      const endAngle = Math.max(visualFacingAngle, tangentAngle);
      const arcSpan = endAngle - startAngle > Math.PI ? 
        (2 * Math.PI - (endAngle - startAngle)) : (endAngle - startAngle);
      const numSegments = Math.max(8, Math.floor(arcSpan * 10));
      
      // Generate arc points
      const arcCenter = this.character.position.clone();
      arcCenter.y += 0.5; // Above character
      
      for (let i = 0; i <= numSegments; i++) {
        const t = i / numSegments;
        const angle = startAngle + t * arcSpan;
        arcPoints.push(new Vector3(
          arcCenter.x + Math.sin(angle) * arcRadius,
          arcCenter.y,
          arcCenter.z + Math.cos(angle) * arcRadius
        ));
      }
      
      if (arcPoints.length >= 2) {
        this.debugAngleArc = MeshBuilder.CreateLines(`angleArc_${this.characterId}`, {
          points: arcPoints,
          updatable: false
        }, this.scene);
        
        // Color based on alignment: GREEN = aligned, YELLOW = small diff, RED = large diff
        let arcColor: Color3;
        if (angleDiffDegrees < 5) {
          arcColor = new Color3(0, 1, 0); // GREEN - well aligned
        } else if (angleDiffDegrees < 30) {
          arcColor = new Color3(1, 1, 0); // YELLOW - small difference
        } else {
          arcColor = new Color3(1, 0, 0); // RED - large difference
        }
        
        (this.debugAngleArc as any).color = arcColor;
        this.debugAngleArc.layerMask = 0x20000000; // Debug layer
        this.debugAngleArc.isPickable = false;
        this.debugAngleArc.checkCollisions = false;
      }
      
      // Update current segment visualization - red sphere at projection point
      const projectionPoint = projection.splinePoint;
      
      if (this.debugCurrentSegment) {
        this.debugCurrentSegment.dispose();
      }
      
      this.debugCurrentSegment = MeshBuilder.CreateSphere('currentSegment', {
        diameter: 0.4,
        segments: 16
      }, this.scene);
      
      this.debugCurrentSegment.position.copyFrom(projectionPoint);
      
      const segmentMat = new StandardMaterial('segmentMat', this.scene);
      segmentMat.emissiveColor = new Color3(1, 0, 0); // RED
      segmentMat.disableLighting = true;
      this.debugCurrentSegment.material = segmentMat;
      this.debugCurrentSegment.layerMask = 0x20000000; // Debug layer - filterMesh excludes from shadows
      this.debugCurrentSegment.isPickable = false;
      this.debugCurrentSegment.checkCollisions = false;
      this.debugCurrentSegment.receiveShadows = false;
      this.debugCurrentSegment.metadata = { isDebug: true, noShadows: true };
    }
    
    // Z-axis line is parented to character capsule - no manual updates needed!
    
    // Update velocity line if character is moving
    const speed = this.velocity.length();
    if (speed > 0.1) {
      if (!this.debugVelocityLine) {
        const debugRenderer = DebugRenderer.getInstance();
        const startPoint = this.character.position.add(new Vector3(0, 1, 0));
        const endPoint = startPoint.add(this.velocity.normalize());
        this.debugVelocityLine = debugRenderer.createLine([startPoint, endPoint], new Color3(1, 0, 1));
      }
      
      // Update velocity line
      if (this.debugVelocityLine) {
        const startPoint = this.character.position.add(new Vector3(0, 1, 0));
        const velocityDir = this.velocity.normalize();
        const endPoint = startPoint.add(velocityDir.scale(Math.min(speed, 2)));
        
        // Recreate line with new points
        const debugRenderer = DebugRenderer.getInstance();
        debugRenderer.remove(this.debugVelocityLine);
        this.debugVelocityLine = debugRenderer.createLine([startPoint, endPoint], new Color3(1, 0, 1));
      }
    } else {
      // Remove velocity line when not moving
      if (this.debugVelocityLine) {
        const debugRenderer = DebugRenderer.getInstance();
        debugRenderer.remove(this.debugVelocityLine);
        this.debugVelocityLine = null;
      }
    }
  }

  /**
   * Cleanup character debug meshes
   */
  private cleanupCharacterDebugMeshes(): void {
    const debugRenderer = DebugRenderer.getInstance();
    
    if (this.debugFacingLabel) {
      debugRenderer.remove(this.debugFacingLabel);
      this.debugFacingLabel = null;
    }
    
    if (this.debugFacingArrow) {
      debugRenderer.remove(this.debugFacingArrow);
      this.debugFacingArrow = null;
    }

    if (this.debugFacingAnchor) {
      this.debugFacingAnchor.dispose();
      this.debugFacingAnchor = null;
    }
    
    if (this.debugVelocityLine) {
      debugRenderer.remove(this.debugVelocityLine);
      this.debugVelocityLine = null;
    }
    
    if (this.debugTangentLabel) {
      debugRenderer.remove(this.debugTangentLabel);
      this.debugTangentLabel = null;
    }
    
    if (this.debugTangentArrow) {
      debugRenderer.remove(this.debugTangentArrow);
      this.debugTangentArrow = null;
    }
    
    if (this.debugCurrentSegment) {
      this.debugCurrentSegment.dispose();
      this.debugCurrentSegment = null;
    }
    
    if (this.debugAngleArc) {
      this.debugAngleArc.dispose();
      this.debugAngleArc = null;
    }
  }

  public dispose(): void {
    logger.character.debug(`Disposing character: ${this.characterId}`);
    
    // Cleanup XState actor (Sprint 0023)
    if (this.xstateActor) {
      const stateManager = StateManager.getInstance();
      stateManager.removeCharacter(this.characterId);
      ActionHandlerRegistry.unregisterAnimationController(this.characterId);
      ActionHandlerRegistry.unregisterCharacter(this.characterId);
      this.xstateActor = null;
    }
    
    // Cleanup debug meshes
    this.cleanupCharacterDebugMeshes();
    // Dispose cloned assets (not shared from cache)
    if (this.characterAsset) {
      this.characterAsset.rootMesh.dispose();
      this.characterAsset.meshes.forEach(mesh => mesh.dispose());
      if (this.characterAsset.skeleton) {
        this.characterAsset.skeleton.dispose();
      }
      // Dispose cloned submeshes
      this.characterAsset.submeshes.forEach(submesh => {
        submesh.rootMesh.dispose();
      });
      // Don't dispose animations - they're shared from cache
    }
    if (this.characterVisualRoot) {
      this.characterVisualRoot.dispose();
    }
    this.character.dispose();
  }
}
